import { FunctionComponent, useMemo, useState } from "react";
import GenericWheelpicker, { GenericWheelpickerType } from "../generic";
import { DropdownItemType } from "../../../../atoms/dropdown/container";

interface TimeWheelpickerProps extends GenericWheelpickerType {
  format?: "12" | "24";
}

export const TimeWheelpicker: FunctionComponent<
  Omit<TimeWheelpickerProps, "itemWidth">
> = ({ format = "12", selected, ...props }) => {
  // Generate time data based on format
  const timeData = useMemo(() => {
    const hours =
      format === "24"
        ? Array.from({ length: 24 }, (_, i) => ({
            label: String(i).padStart(2, "0"),
            value: String(i).padStart(2, "0"),
          }))
        : Array.from({ length: 12 }, (_, i) => ({
            label: String(i + 1).padStart(2, "0"),
            value: String(i + 1).padStart(2, "0"),
          }));
    const minutes = Array.from({ length: 60 }, (_, i) => ({
      label: String(i).padStart(2, "0"),
      value: String(i).padStart(2, "0"),
    }));
    const ampm =
      format === "12"
        ? [
            { label: "AM", value: "AM" },
            { label: "PM", value: "PM" },
          ]
        : undefined;
    return ampm
      ? [{ items: hours }, { items: minutes, extra: ":" }, { items: ampm }]
      : [{ items: hours }, { items: minutes, extra: ":" }];
  }, [format]);

  // Default selected values
  const defaultSelected = useMemo(() => {
    const hours = timeData[0].items;
    const minutes = timeData[1].items;
    const ampm = timeData[2]?.items;
    return ampm ? [hours[0], minutes[0], ampm[0]] : [hours[0], minutes[0]];
  }, [timeData]);

  const [value, setValue] = useState<DropdownItemType[]>(defaultSelected);

  return (
    <GenericWheelpicker
      textAlignment="center"
      {...props}
      data={timeData}
      selected={selected || defaultSelected}
      customValue={`${value[0].label}:${value[1].label} ${value[2]?.label}`}
      onChange={(e: DropdownItemType | DropdownItemType[]) =>
        setValue(e as DropdownItemType[])
      }
      itemWidth="fit-content"
    />
  );
};

TimeWheelpicker.displayName = "TimeWheelpicker";

export default TimeWheelpicker;
